import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, ArrowLeft, Send, Loader2, Package, Upload, Download, X, CheckCircle, AlertTriangle, List } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getBuyerCart, removeFromCartApi, clearCartApi, submitCartApi, importCsvToCart, getCartImportTemplateUrl } from '../../lib/api';
import { Toaster, toast } from 'sonner';
import SaveAsListModal from '../../components/SaveAsListModal';

export default function BuyerCartPage() {
  const { refreshCart, t } = useApp();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showSaveListModal, setShowSaveListModal] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const res = await getBuyerCart();
      setCart(res.data);
    } catch (err) {
      console.error('Failed to fetch cart:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCartApi(productId);
      toast.success(t('cart.removed'));
      fetchCart();
      refreshCart();
    } catch (err) {
      toast.error(t('cart.failedRemove'));
    }
  };

  const handleClear = async () => {
    try {
      await clearCartApi();
      setCart({ items: [], total: 0 });
      toast.success(t('cart.cleared'));
      refreshCart();
    } catch (err) {
      toast.error(t('cart.failedClear'));
    }
  };

  const handleSubmit = async () => {
    if (cart.items.length === 0) return;
    setSubmitting(true);
    try {
      const res = await submitCartApi();
      toast.success(`${res.data.quote_number} — Quote request submitted!`);
      refreshCart();
      navigate('/buyer');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('cart.failedSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await importCsvToCart(formData);
      setImportResult(res.data);
      if (res.data.added > 0) {
        toast.success(`${res.data.added} ${t('cart.addedToCart')}`);
        fetchCart();
        refreshCart();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || t('cart.importFailed'));
      setImportResult({ added: 0, already_in_cart: 0, skipped: 0, errors: [{ row: '-', unique_number: '-', reason: detail || 'unknown_error' }] });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const token = localStorage.getItem('luxortek_buyer_token');
    const url = getCartImportTemplateUrl();
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'luxortek_cart_import_template.csv';
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error(t('cart.failedDownload')));
  };

  const openImportModal = () => {
    setImportFile(null);
    setImportResult(null);
    setShowImportModal(true);
  };

  const getGradeBadge = (grade) => {
    const g = grade?.toUpperCase() || '';
    if (g.includes('A')) return 'grade-a';
    if (g.includes('B')) return 'grade-b';
    if (g.includes('C')) return 'grade-c';
    return 'bg-gray-600';
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="buyer-cart-page">
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/buyer" className="text-white/60 hover:text-white" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <ShoppingCart className="w-7 h-7 text-[#00D4FF]" />
                {t('cart.title')}
              </h1>
              <p className="text-white/50 text-sm mt-1">{cart.items.length} {cart.items.length === 1 ? t('common.item') : t('common.items')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={openImportModal}
              className="btn-secondary flex items-center gap-2 text-sm"
              data-testid="bulk-import-btn"
            >
              <Upload className="w-4 h-4" />
              {t('cart.bulkImportCsv')}
            </button>
            {cart.items.length > 0 && (
              <button
                onClick={() => setShowSaveListModal(true)}
                className="btn-secondary flex items-center gap-2 text-sm"
                data-testid="save-as-list-btn"
              >
                <List className="w-4 h-4" />
                {t('savedLists.saveFromCart')}
              </button>
            )}
            {cart.items.length > 0 && (
              <button
                onClick={handleClear}
                className="text-sm text-white/50 hover:text-red-400 transition-colors"
                data-testid="clear-cart-btn"
              >
                {t('cart.clearCart')}
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="card-dark p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#00D4FF]" />
          </div>
        ) : cart.items.length === 0 ? (
          <div className="card-dark p-12 text-center" data-testid="empty-cart">
            <Package className="w-16 h-16 text-white/10 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('cart.empty')}</h2>
            <p className="text-white/50 mb-6">{t('cart.emptyMsg')}</p>
            <Link to="/products" className="btn-primary inline-block" data-testid="browse-products-btn">
              {t('cart.browseInventory')}
            </Link>
          </div>
        ) : (
          <>
            {/* Cart Items Table */}
            <div className="card-dark overflow-hidden mb-6" data-testid="cart-items-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#0A0A0A] border-b border-white/10">
                      <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">{t('cart.uniqueNum')}</th>
                      <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">{t('cart.brand')}</th>
                      <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">{t('cart.model')}</th>
                      <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">{t('cart.specs')}</th>
                      <th className="p-4 text-center text-xs font-semibold text-white/60 uppercase tracking-wider">{t('cart.grade')}</th>
                      <th className="p-4 text-right text-xs font-semibold text-white/60 uppercase tracking-wider">{t('cart.price')}</th>
                      <th className="p-4 text-center text-xs font-semibold text-white/60 uppercase tracking-wider">{t('cart.remove')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.items.map((item) => (
                      <tr key={item.product_id} className="border-b border-white/5 hover:bg-[#00D4FF]/5" data-testid={`cart-item-${item.product_id}`}>
                        <td className="p-4 font-mono text-[#00D4FF] font-semibold">{item.unique_id}</td>
                        <td className="p-4 text-white font-medium">{item.oem}</td>
                        <td className="p-4 text-white/90">{item.model}</td>
                        <td className="p-4 text-white/60 text-sm">
                          {[item.cpu, item.ram, item.hard_drive].filter(Boolean).join(' / ') || '-'}
                        </td>
                        <td className="p-4 text-center">
                          {item.grade && (
                            <span className={`px-2 py-1 text-xs font-bold text-white ${getGradeBadge(item.grade)}`}>
                              {item.grade}
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-right font-mono font-bold text-[#00D4FF]">
                          ${item.sale_price?.toLocaleString()}
                        </td>
                        <td className="p-4 text-center">
                          <button
                            onClick={() => handleRemove(item.product_id)}
                            className="p-2 text-white/40 hover:text-red-400 transition-colors"
                            data-testid={`remove-${item.product_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Order Summary */}
            <div className="card-dark p-6" data-testid="cart-summary">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="text-white/50 text-sm">{t('cart.total')} ({cart.items.length} {t('common.units')})</div>
                  <div className="text-3xl font-bold font-mono text-[#00D4FF]" data-testid="cart-total">
                    ${cart.total?.toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Link to="/products" className="btn-secondary text-sm" data-testid="continue-shopping-btn">
                    {t('cart.continueShopping')}
                  </Link>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="btn-primary flex items-center gap-2"
                    data-testid="submit-order-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {submitting ? 'Submitting...' : 'Request Quote'}
                  </button>
                </div>
              </div>
              <p className="text-white/40 text-xs mt-3">
                {t('cart.supplierNotice')}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Bulk Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="import-modal-overlay">
          <div className="bg-[#111] border border-white/10 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" data-testid="import-modal">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Upload className="w-5 h-5 text-[#00D4FF]" />
                {t('cart.bulkImportCsv')}
              </h2>
              <button onClick={() => setShowImportModal(false)} className="text-white/40 hover:text-white" data-testid="close-import-modal">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="bg-[#0A0A0A] border border-white/10 p-4">
                <p className="text-white/60 text-sm mb-3">{t('cart.csvInstructions')} <span className="text-white font-mono">unique_number</span> {t('cart.csvInstructions2')}</p>
                <button
                  onClick={handleDownloadTemplate}
                  className="text-[#00D4FF] text-sm hover:underline flex items-center gap-1"
                  data-testid="download-template-btn"
                >
                  <Download className="w-3.5 h-3.5" />
                  {t('cart.downloadTemplate')}
                </button>
              </div>

              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => { setImportFile(e.target.files[0] || null); setImportResult(null); }}
                  data-testid="csv-file-input"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full p-4 border-2 border-dashed border-white/15 hover:border-[#00D4FF]/40 text-center transition-colors cursor-pointer"
                  data-testid="file-picker-btn"
                >
                  {importFile ? (
                    <div className="flex items-center justify-center gap-2 text-white">
                      <CheckCircle className="w-4 h-4 text-[#10B981]" />
                      <span className="text-sm font-medium">{importFile.name}</span>
                      <span className="text-white/40 text-xs">({(importFile.size / 1024).toFixed(1)} KB)</span>
                    </div>
                  ) : (
                    <div className="text-white/40 text-sm">{t('cart.selectFile')}</div>
                  )}
                </button>
              </div>

              <button
                onClick={handleImport}
                disabled={!importFile || importing}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="run-import-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {importing ? t('cart.importing') : t('cart.importToCart')}
              </button>

              {importResult && (
                <div className="space-y-3" data-testid="import-results">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-[#10B981]/10 border border-[#10B981]/20 p-3 text-center">
                      <div className="text-xl font-bold text-[#10B981] font-mono" data-testid="import-added-count">{importResult.added}</div>
                      <div className="text-white/50 text-xs">{t('cart.added')}</div>
                    </div>
                    <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/20 p-3 text-center">
                      <div className="text-xl font-bold text-[#F59E0B] font-mono" data-testid="import-already-count">{importResult.already_in_cart}</div>
                      <div className="text-white/50 text-xs">{t('cart.alreadyInCart')}</div>
                    </div>
                    <div className="bg-[#EF4444]/10 border border-[#EF4444]/20 p-3 text-center">
                      <div className="text-xl font-bold text-[#EF4444] font-mono" data-testid="import-skipped-count">{importResult.skipped}</div>
                      <div className="text-white/50 text-xs">{t('cart.errors')}</div>
                    </div>
                  </div>

                  {importResult.errors?.length > 0 && (
                    <div className="bg-[#0A0A0A] border border-white/10 p-4 max-h-40 overflow-y-auto" data-testid="import-errors-table">
                      <div className="flex items-center gap-2 text-[#EF4444] text-sm font-medium mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        {t('cart.errors')} ({importResult.errors.length})
                      </div>
                      <div className="space-y-1">
                        {importResult.errors.map((err, i) => (
                          <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0">
                            <span className="text-white/60">{t('cart.row')} {err.row}: <span className="text-white font-mono">{err.unique_number || '(empty)'}</span></span>
                            <span className="text-[#EF4444]">{err.reason.replace(/_/g, ' ')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showSaveListModal && cart.items.length > 0 && (
        <SaveAsListModal
          items={cart.items.map(item => ({ product_id: item.product_id, qty: 1 }))}
          onClose={() => setShowSaveListModal(false)}
        />
      )}
    </div>
  );
}
